import { useState } from 'react';
import { useCreateAgent } from '../../hooks/useAgents';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

interface CreateAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateAgentModal({ isOpen, onClose }: CreateAgentModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    skills: '',
    languages: '',
    timezone: 'America/New_York',
    targetUtilization: '85',
  });

  const createAgent = useCreateAgent();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Generate employeeId from email (e.g., "john.doe@example.com" -> "john.doe")
      const employeeId = formData.email.split('@')[0];

      await createAgent.mutateAsync({
        employeeId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
        languages: formData.languages.split(',').map((l) => l.trim()).filter(Boolean),
        timezone: formData.timezone,
        targetUtilization: parseInt(formData.targetUtilization),
      });

      // Reset form and close
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        skills: '',
        languages: '',
        timezone: 'America/New_York',
        targetUtilization: '85',
      });
      onClose();
    } catch (error) {
      console.error('Error creating agent:', error);
      alert('Failed to create agent. Please try again.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Agent"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={createAgent.isPending}
          >
            Create Agent
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            required
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          />
          <Input
            label="Last Name"
            required
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          />
        </div>

        <Input
          label="Email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />

        <Input
          label="Skills"
          placeholder="customer-service, technical-support, sales"
          helperText="Comma-separated list of skills"
          value={formData.skills}
          onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
        />

        <Input
          label="Languages"
          placeholder="English, Spanish, French"
          helperText="Comma-separated list of languages"
          value={formData.languages}
          onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
        />

        <Select
          label="Timezone"
          required
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          options={[
            { value: 'America/New_York', label: 'Eastern Time (ET)' },
            { value: 'America/Chicago', label: 'Central Time (CT)' },
            { value: 'America/Denver', label: 'Mountain Time (MT)' },
            { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
            { value: 'Europe/London', label: 'London (GMT)' },
            { value: 'Europe/Paris', label: 'Paris (CET)' },
            { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
          ]}
        />

        <Input
          label="Target Utilization (%)"
          type="number"
          min="0"
          max="100"
          required
          value={formData.targetUtilization}
          onChange={(e) => setFormData({ ...formData, targetUtilization: e.target.value })}
          helperText="Expected percentage of productive time"
        />
      </form>
    </Modal>
  );
}
